import React, { useState, useEffect } from 'react';
import { FiDollarSign, FiTrendingUp, FiAward, FiCheckCircle } from 'react-icons/fi';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { parentAnalyticsAPI } from '../../../api/parentEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Section,
  SectionTitle,
  Grid,
  Card,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Badge,
  ChartContainer,
  LoadingContainer,
  EmptyState,
} from '../Analytics.styles';

const ROIMetrics = ({ studentId }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await parentAnalyticsAPI.getROIMetrics(studentId);
      setData(response.data.roi_metrics);
    } catch (error) {
      showToast('error', 'Failed to load ROI metrics');
      console.error('Error fetching ROI metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'KES') => {
    return `${currency} ${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getROIScoreLabel = (score) => {
    if (score >= 0.05) return { label: 'Excellent', color: theme.colors.success };
    if (score >= 0.03) return { label: 'Good', color: theme.colors.info };
    if (score >= 0.01) return { label: 'Fair', color: theme.colors.warning };
    return { label: 'Developing', color: theme.colors.textSecondary };
  };

  const prepareSpendingByCategory = () => {
    if (!data || !data.spending || !data.spending.by_category) return [];
    
    return data.spending.by_category.map((cat) => ({
      name: cat.category.charAt(0).toUpperCase() + cat.category.slice(1),
      value: cat.amount,
      percentage: cat.percentage,
      transactions: cat.transactions,
    }));
  };

  const prepareMonthlyTrend = () => {
    if (!data || !data.spending || !data.spending.monthly_trend) return [];
    
    return data.spending.monthly_trend.map((month) => ({
      month: new Date(month.month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      amount: month.amount,
    }));
  };

  const prepareEfficiencyComparison = () => {
    if (!data || !data.efficiency) return [];
    
    return [
      { metric: 'Cost per Course', value: data.efficiency.cost_per_course },
      { metric: 'Cost per Quiz Passed', value: data.efficiency.cost_per_quiz_passed },
      { metric: 'Cost per Hour', value: data.efficiency.cost_per_hour },
    ];
  };

  const COLORS = [
    theme.colors.primary,
    theme.colors.secondary,
    theme.colors.accent,
    theme.colors.info,
    theme.colors.success,
    '#8B5CF6',
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '8px',
          padding: '12px',
          boxShadow: theme.shadows.lg,
        }}>
          <p style={{ margin: 0, fontWeight: 600, color: theme.colors.textPrimary }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '4px 0 0 0', color: entry.color, fontSize: '14px' }}>
              {entry.name}: {formatCurrency(entry.value, data?.spending?.currency || 'KES')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading ROI metrics..." />
      </LoadingContainer>
    );
  }

  if (!data || !data.spending) {
    return (
      <EmptyState>
        <FiDollarSign size={64} />
        <h3>No ROI Data</h3>
        <p>ROI metrics will appear once spending records are added</p>
      </EmptyState>
    );
  }

  const roiScoreInfo = getROIScoreLabel(data.efficiency.roi_score);

  return (
    <>
      {/* Value Comparison - Hero Section */}
      {data.value_comparison && (
        <Section>
          <Card
            style={{
              background: `linear-gradient(135deg, ${theme.colors.success}15, ${theme.colors.primary}15)`,
              border: `2px solid ${theme.colors.success}`,
              padding: '32px',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>💰</div>
              <h2 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: theme.colors.success,
                margin: '0 0 8px 0',
              }}>
                {formatCurrency(data.value_comparison.savings, data.spending.currency)} Saved!
              </h2>
              <p style={{
                fontSize: '20px',
                color: theme.colors.textPrimary,
                margin: '0 0 24px 0',
              }}>
                You saved {data.value_comparison.savings_percentage.toFixed(1)}% vs. traditional tutoring!
              </p>
              
              <Grid minWidth="200px" style={{ marginTop: '24px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', color: theme.colors.textSecondary, marginBottom: '8px' }}>
                    Platform Cost
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.primary }}>
                    {formatCurrency(data.value_comparison.platform_cost, data.spending.currency)}
                  </div>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', color: theme.colors.textSecondary, marginBottom: '8px' }}>
                    Traditional Tutoring
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.error }}>
                    {formatCurrency(data.value_comparison.traditional_tutoring_cost, data.spending.currency)}
                  </div>
                </div>
              </Grid>
            </div>
          </Card>
        </Section>
      )}

      {/* Overall Spending & ROI Score */}
      <Section>
        <SectionTitle>
          <FiDollarSign />
          Investment Overview
        </SectionTitle>
        <Grid minWidth="250px">
          <StatCard>
            <StatLabel>Total Investment</StatLabel>
            <StatValue color={theme.colors.primary}>
              {formatCurrency(data.spending.total, data.spending.currency)}
            </StatValue>
            <StatDescription>
              Total spending on learning
            </StatDescription>
          </StatCard>

          <StatCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <StatLabel>ROI Score</StatLabel>
              <Badge color={roiScoreInfo.color}>
                {roiScoreInfo.label}
              </Badge>
            </div>
            <StatValue color={roiScoreInfo.color}>
              {data.efficiency.roi_score.toFixed(4)}
            </StatValue>
            <StatDescription>
              Value per dollar spent
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Completed Courses</StatLabel>
            <StatValue color={theme.colors.success}>
              {data.outcomes.completed_courses}
            </StatValue>
            <StatDescription>
              Educational milestones achieved
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Quizzes Passed</StatLabel>
            <StatValue color={theme.colors.info}>
              {data.outcomes.quizzes_passed}
            </StatValue>
            <StatDescription>
              Knowledge assessments completed
            </StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {/* Spending Breakdown */}
      <Section>
        <SectionTitle>
          Spending Breakdown by Category
        </SectionTitle>
        <Grid minWidth="450px">
          <Card>
            <ChartContainer height="400px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={prepareSpendingByCategory()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage.toFixed(0)}%`}
                    outerRadius={120}
                    fill={theme.colors.primary}
                    dataKey="value"
                  >
                    {prepareSpendingByCategory().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>

          <Card>
            <h3 style={{ margin: '0 0 16px 0', color: theme.colors.textPrimary }}>
              Category Details
            </h3>
            {prepareSpendingByCategory().map((category, index) => (
              <div
                key={index}
                style={{
                  marginBottom: '16px',
                  padding: '16px',
                  background: theme.colors.backgroundSecondary,
                  borderRadius: '8px',
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: COLORS[index % COLORS.length],
                      }}
                    />
                    <span style={{ fontWeight: 600, color: theme.colors.textPrimary }}>
                      {category.name}
                    </span>
                  </div>
                  <Badge color={COLORS[index % COLORS.length]}>
                    {category.percentage.toFixed(0)}%
                  </Badge>
                </div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: theme.colors.textPrimary, marginBottom: '4px' }}>
                  {formatCurrency(category.value, data.spending.currency)}
                </div>
                <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                  {category.transactions} transactions
                </div>
              </div>
            ))}
          </Card>
        </Grid>
      </Section>

      {/* Monthly Spending Trend */}
      {data.spending.monthly_trend && data.spending.monthly_trend.length > 0 && (
        <Section>
          <SectionTitle>
            <FiTrendingUp />
            Monthly Spending Trend
          </SectionTitle>
          <Card>
            <ChartContainer height="400px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={prepareMonthlyTrend()}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: theme.colors.textSecondary }}
                  />
                  <YAxis
                    tick={{ fill: theme.colors.textSecondary }}
                    label={{
                      value: `Amount (${data.spending.currency})`,
                      angle: -90,
                      position: 'insideLeft',
                      fill: theme.colors.textSecondary,
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke={theme.colors.primary}
                    strokeWidth={3}
                    dot={{ r: 6, fill: theme.colors.primary }}
                    name="Spending"
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>
        </Section>
      )}

      {/* Learning Outcomes */}
      <Section>
        <SectionTitle>
          <FiAward />
          Learning Outcomes
        </SectionTitle>
        <Grid minWidth="250px">
          <StatCard>
            <StatLabel>Learning Hours</StatLabel>
            <StatValue color={theme.colors.accent}>
              {data.outcomes.learning_hours.toFixed(1)}h
            </StatValue>
            <StatDescription>
              Total time invested in learning
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Badges Earned</StatLabel>
            <StatValue style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '32px' }}>🏆</span>
              {data.outcomes.badges_earned}
            </StatValue>
            <StatDescription>
              Achievement milestones
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Average Performance</StatLabel>
            <StatValue color={theme.colors.success}>
              {data.outcomes.average_performance.toFixed(1)}%
            </StatValue>
            <StatDescription>
              Overall academic performance
            </StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {/* Efficiency Metrics */}
      <Section>
        <SectionTitle>
          <FiCheckCircle />
          Cost Efficiency Analysis
        </SectionTitle>
        <Grid minWidth="300px">
          <StatCard>
            <StatLabel>Cost per Course</StatLabel>
            <StatValue color={theme.colors.primary}>
              {formatCurrency(data.efficiency.cost_per_course, data.spending.currency)}
            </StatValue>
            <StatDescription>
              Average investment per completed course
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Cost per Quiz Passed</StatLabel>
            <StatValue color={theme.colors.info}>
              {formatCurrency(data.efficiency.cost_per_quiz_passed, data.spending.currency)}
            </StatValue>
            <StatDescription>
              Investment per successful assessment
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Cost per Learning Hour</StatLabel>
            <StatValue color={theme.colors.accent}>
              {formatCurrency(data.efficiency.cost_per_hour, data.spending.currency)}
            </StatValue>
            <StatDescription>
              Hourly learning investment rate
            </StatDescription>
          </StatCard>
        </Grid>

        <Card style={{ marginTop: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', color: theme.colors.textPrimary }}>
            Efficiency Comparison
          </h3>
          <ChartContainer height="300px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={prepareEfficiencyComparison()}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                <XAxis
                  dataKey="metric"
                  tick={{ fill: theme.colors.textSecondary, fontSize: 12 }}
                  angle={-15}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  tick={{ fill: theme.colors.textSecondary }}
                  label={{
                    value: `Cost (${data.spending.currency})`,
                    angle: -90,
                    position: 'insideLeft',
                    fill: theme.colors.textSecondary,
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="value"
                  fill={theme.colors.primary}
                  name="Cost"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>
      </Section>

      {/* Investment Insight */}
      <Section>
        <Card
          style={{
            background: theme.colors.backgroundSecondary,
            padding: '24px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>💡</div>
          <h3 style={{ margin: '0 0 12px 0', color: theme.colors.textPrimary }}>
            Investment Insight
          </h3>
          <p style={{ margin: 0, fontSize: '16px', color: theme.colors.textSecondary, lineHeight: '1.6' }}>
            Your investment of {formatCurrency(data.spending.total, data.spending.currency)} has resulted in{' '}
            <strong>{data.outcomes.completed_courses} completed courses</strong> and{' '}
            <strong>{data.outcomes.learning_hours.toFixed(1)} hours</strong> of quality learning.
            This represents excellent value compared to traditional tutoring methods.
          </p>
        </Card>
      </Section>
    </>
  );
};

export default ROIMetrics;

